<template>
  <div class="facebook-binding">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>{{ $t('facebook.title') }}</span>
      </div>

      <div class="binding-content">
        <div class="binding-status">
          <el-alert
            v-if="bindingStatus.bound && bindingStatus.status === 0"
            :title="$t('facebook.authExpired')"
            type="warning"
            :closable="false"
            show-icon>
            <div slot="description">
              <p>{{ $t('facebook.authExpiredDesc') }}</p>
            </div>
          </el-alert>

          <el-alert
            v-else-if="bindingStatus.bound"
            :title="$t('facebook.authSuccess')"
            type="success"
            :closable="false"
            show-icon>
            <div slot="description">
              <p>{{ bindingStatus.message }}</p>
            </div>
          </el-alert>

          <el-alert
            v-else
            :title="$t('facebook.notBound')"
            type="info"
            :closable="false"
            show-icon>
            <div slot="description">
              <p>{{ bindingStatus.message || $t('facebook.notBoundDesc') }}</p>
            </div>
          </el-alert>
        </div>

        <div class="binding-actions">
          <div v-if="!bindingStatus.bound" class="center">
            <el-button
              type="primary"
              :loading="bindingLoading"
              @click="handleBindFacebook"
              icon="el-icon-link">
              {{ $t('facebook.bindFacebook') }}
            </el-button>
            <el-button
              type="success"
              :loading="tokenBindingLoading"
              @click="showTokenDialog = true"
              icon="el-icon-key"
              style="margin-left: 10px;">
              {{ $t('facebook.addToken') }}
            </el-button>
          </div>
          <div v-else class="center">
            <el-button
              type="danger"
              :loading="unbindingLoading"
              @click="handleUnbindFacebook"
              icon="el-icon-delete">
              {{ $t('facebook.unbind') }}
            </el-button>
            <el-button
              type="primary"
              :loading="refreshLoading"
              @click="handleRefreshBm"
              icon="el-icon-refresh">
              {{ $t('facebook.refreshBm') }}
            </el-button>
          </div>
        </div>

        <div v-if="bindingStatus.bound && accounts.length > 0" class="account-list">
          <div class="account-list-header">
            <h4>{{ $t('facebook.accountCount', { count: accounts.length }) }}</h4>
            <div class="add-account-actions">
              <el-button size="small" type="primary" @click="handleBindFacebook" icon="el-icon-link">
                {{ $t('facebook.addOAuth') }}
              </el-button>
              <el-button size="small" type="success" @click="showTokenDialog = true" icon="el-icon-key">
                {{ $t('facebook.addToken') }}
              </el-button>
            </div>
          </div>
          <el-table :data="accounts" stripe style="width: 100%">
            <el-table-column :label="$t('facebook.accountName')" width="220">
              <template slot-scope="scope">
                <div v-if="scope.row.editing">
                  <el-input
                    v-model="scope.row.editTokenName"
                    size="mini"
                    :placeholder="$t('facebook.tokenNamePlaceholder')"
                    @blur="handleSaveTokenName(scope.row)"
                    @keyup.enter.native="handleSaveTokenName(scope.row)"
                    @keyup.esc.native="handleCancelEdit(scope.row)">
                  </el-input>
                </div>
                <div v-else>
                  <strong>{{ scope.row.tokenName || 'Facebook' }}</strong>
                  <br>
                  <small>ID: {{ scope.row.platformUserId }}</small>
                </div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('facebook.bindType')" width="120">
              <template slot-scope="scope">
                <el-tag :type="scope.row.bindType === 1 ? 'primary' : 'success'">
                  {{ scope.row.bindType === 1 ? $t('facebook.bindTypeOAuth') : $t('facebook.bindTypeToken') }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.status')" width="100">
              <template slot-scope="scope">
                <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
                  {{ scope.row.status === 1 ? $t('facebook.statusActive') : $t('facebook.statusInactive') }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column :label="$t('facebook.primaryAccount')" width="120">
              <template slot-scope="scope">
                <el-radio
                  v-model="primaryAccountId"
                  :label="scope.row.tokenId"
                  @change="handleSetPrimary(scope.row.tokenId)">
                  {{ scope.row.isPrimary === 1 ? $t('common.yes') : $t('common.no') }}
                </el-radio>
              </template>
            </el-table-column>
            <el-table-column :label="$t('facebook.bindTime')" width="160">
              <template slot-scope="scope">
                {{ scope.row.createTime || '-' }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.operation')" width="200">
              <template slot-scope="scope">
                <el-button size="mini" type="text" @click="handleEditTokenName(scope.row)" v-if="!scope.row.editing">
                  {{ $t('facebook.editName') }}
                </el-button>
                <el-button size="mini" type="text" @click="handleSaveTokenName(scope.row)" v-if="scope.row.editing">
                  {{ $t('facebook.saveName') }}
                </el-button>
                <el-button size="mini" type="text" @click="handleCancelEdit(scope.row)" v-if="scope.row.editing">
                  {{ $t('common.cancel') }}
                </el-button>
                <el-button size="mini" type="text" @click="handleDeleteAccount(scope.row.tokenId)">
                  {{ $t('common.delete') }}
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </el-card>

    <el-dialog
      :title="$t('facebook.tokenDialogTitle')"
      :visible.sync="showTokenDialog"
      width="500px"
      :close-on-click-modal="false">
      <el-form :model="tokenForm" :rules="tokenRules" ref="tokenForm">
        <el-form-item :label="$t('facebook.tokenName')" prop="tokenName">
          <el-input v-model="tokenForm.tokenName" :placeholder="$t('facebook.tokenNamePlaceholder')"></el-input>
        </el-form-item>
        <el-form-item :label="$t('facebook.tokenType')" prop="tokenType">
          <el-select v-model="tokenForm.tokenType">
            <el-option :value="1" :label="$t('facebook.tokenTypeBm')" />
            <el-option :value="2" :label="$t('facebook.tokenTypeAccount')" />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('facebook.adAccountId')" prop="adAccountId" v-if="tokenForm.tokenType === 2">
          <el-input v-model="tokenForm.adAccountId" :placeholder="$t('facebook.adAccountIdPlaceholder')"></el-input>
        </el-form-item>
        <el-form-item :label="$t('facebook.accessToken')" prop="token">
          <el-input
            v-model="tokenForm.token"
            type="textarea"
            :rows="4"
            :placeholder="$t('facebook.accessTokenPlaceholder')">
          </el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showTokenDialog = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="tokenBindingLoading" @click="handleTokenBind">{{ $t('facebook.confirmBind') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  getFacebookPopupAuthUrl,
  getFacebookBindingStatus,
  facebookUnbind,
  facebookBind,
  facebookBindByToken,
  getUserFacebookAccounts,
  setPrimaryAccount,
  deleteFacebookAccount,
  updateTokenName,
  refreshBmAccounts
} from '@/api/meta/facebook';

export default {
  name: 'FacebookBinding',
  data() {
    return {
      bindingStatus: {
        bound: false,
        message: ''
      },
      bindingLoading: false,
      unbindingLoading: false,
      tokenBindingLoading: false,
      refreshLoading: false,
      showTokenDialog: false,
      tokenForm: {
        tokenName: '',
        token: '',
        tokenType: 1,
        adAccountId: ''
      },
      tokenRules: {
        tokenName: [
          { required: true, message: () => this.$t('facebook.tokenRequired'), trigger: 'blur' }
        ],
        tokenType: [
          { required: true, trigger: 'change' }
        ],
        token: [
          { required: true, message: () => this.$t('facebook.tokenRequired'), trigger: 'blur' }
        ]
      },
      accounts: [],
      primaryAccountId: null
    };
  },
  created() {
    this.loadBindingStatus();
    this.loadAccounts();
    this.addMessageListener();
  },
  methods: {
    async loadBindingStatus() {
      const res = await getFacebookBindingStatus();
      this.bindingStatus = res.data || { bound: false, message: '' };
    },
    async loadAccounts() {
      const res = await getUserFacebookAccounts();
      this.accounts = (res.data || []).map(item => ({
        ...item,
        editing: false,
        editTokenName: item.tokenName || ''
      }));
      const primary = this.accounts.find(item => item.isPrimary === 1);
      this.primaryAccountId = primary ? primary.tokenId : null;
    },
    addMessageListener() {
      window.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'facebook_auth_success') {
          this.handleOAuthCallback(event.data.code);
        }
      });
    },
    async handleBindFacebook() {
      this.bindingLoading = true;
      try {
        const res = await getFacebookPopupAuthUrl();
        const url = res.data ? res.data.authorizationUrl : null;
        if (!url) {
          this.$message.error('Failed to get authorization URL');
          return;
        }
        const authWindow = window.open(url, 'facebook_auth', 'width=600,height=700,scrollbars=yes,resizable=yes');
        const checkClosed = setInterval(() => {
          if (authWindow && authWindow.closed) {
            clearInterval(checkClosed);
            this.bindingLoading = false;
            this.loadBindingStatus();
            this.loadAccounts();
          }
        }, 500);
      } catch (e) {
        this.$message.error('Failed to get authorization URL');
      } finally {
        this.bindingLoading = false;
      }
    },
    async handleOAuthCallback(code) {
      try {
        await facebookBind(code, true);
        this.$message.success(this.$t('facebook.bindSuccess'));
        this.loadBindingStatus();
        this.loadAccounts();
      } catch (e) {
        this.$message.error('Bind failed');
      }
    },
    async handleUnbindFacebook() {
      try {
        await this.$confirm(this.$t('facebook.unbindConfirm'), this.$t('consumption.exportTip'));
        this.unbindingLoading = true;
        await facebookUnbind();
        this.$message.success(this.$t('facebook.unbindSuccess'));
        this.loadBindingStatus();
        this.loadAccounts();
      } catch (e) {
        // ignore
      } finally {
        this.unbindingLoading = false;
      }
    },
    async handleTokenBind() {
      this.$refs.tokenForm.validate(async valid => {
        if (!valid) {
          return;
        }
        this.tokenBindingLoading = true;
        try {
          if (this.tokenForm.tokenType === 2 && !this.tokenForm.adAccountId) {
            this.$message.warning(this.$t('facebook.adAccountRequired'));
            this.tokenBindingLoading = false;
            return;
          }
          await facebookBindByToken(this.tokenForm.token, this.tokenForm.tokenName, this.tokenForm.tokenType, this.tokenForm.adAccountId);
          this.$message.success(this.$t('facebook.bindSuccess'));
          this.showTokenDialog = false;
          this.tokenForm = { tokenName: '', token: '', tokenType: 1, adAccountId: '' };
          this.loadBindingStatus();
          this.loadAccounts();
        } catch (e) {
          this.$message.error('Bind failed');
        } finally {
          this.tokenBindingLoading = false;
        }
      });
    },
    async handleSetPrimary(tokenId) {
      await setPrimaryAccount(tokenId);
      this.$message.success(this.$t('facebook.setPrimarySuccess'));
      this.loadAccounts();
    },
    handleEditTokenName(row) {
      row.editing = true;
      row.editTokenName = row.tokenName || '';
    },
    async handleSaveTokenName(row) {
      if (!row.editTokenName) {
        this.$message.warning(this.$t('facebook.tokenRequired'));
        return;
      }
      await updateTokenName(row.tokenId, row.editTokenName);
      row.tokenName = row.editTokenName;
      row.editing = false;
      this.$message.success(this.$t('facebook.nameUpdateSuccess'));
    },
    handleCancelEdit(row) {
      row.editing = false;
      row.editTokenName = row.tokenName || '';
    },
    async handleDeleteAccount(tokenId) {
      try {
        await this.$confirm(this.$t('facebook.deleteConfirm'), this.$t('consumption.exportTip'));
        await deleteFacebookAccount(tokenId);
        this.$message.success(this.$t('facebook.deleteSuccess'));
        this.loadAccounts();
        this.loadBindingStatus();
      } catch (e) {
        // ignore
      }
    },
    async handleRefreshBm() {
      this.refreshLoading = true;
      try {
        await refreshBmAccounts();
        this.$message.success(this.$t('facebook.refreshSuccess'));
      } catch (e) {
        this.$message.error('Refresh failed');
      } finally {
        this.refreshLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.binding-content {
  padding: 8px 4px;
}
.binding-status {
  margin-bottom: 16px;
}
.binding-actions {
  margin-bottom: 16px;
}
.center {
  text-align: center;
}
.account-list {
  margin-top: 16px;
}
.account-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
</style>
